import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../components/ui/card";
import type { RMDesempenho } from "../../../../types/desempenho";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";

interface RMTabProps {
  data: RMDesempenho | null;
}

const CustomChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <div className="bg-card border border-border rounded-lg px-3 py-2 text-sm shadow-md">
        <p className="font-medium text-foreground">{label}</p>
        {payload.map((entry: any, idx: number) => (
          <p key={idx} className="text-muted-foreground">
            {entry.name}: <span className="font-bold" style={{ color: entry.color }}>{Number(entry.value).toLocaleString('pt-BR')}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Normaliza data para string YYYY-MM-DD independente de vir Date ou string
const toDateStr = (val: any): string => String(val).split('T')[0];
const formatDateBR = (dateStr: string): string => {
  const parts = dateStr.split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dateStr;
};

export function RMTab({ data }: RMTabProps) {
  const [expandedDias, setExpandedDias] = useState<Set<string>>(new Set());

  const toggleDia = (dia: string) => {
    setExpandedDias(prev => {
      const next = new Set(prev);
      next.has(dia) ? next.delete(dia) : next.add(dia);
      return next;
    });
  };

  if (!data) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Nenhum dado disponível. Use os filtros acima para buscar informações.
        </CardContent>
      </Card>
    );
  }

  const formatMinutos = (minutos: number) => {
    const horas = Math.floor(minutos / 60);
    const mins = Math.round(minutos % 60);
    return `${horas}h ${mins}m`;
  };

  // Preparar dados dos gráficos por data+hora
  const distribuicaoHorariaData = data.volumetria?.porHora?.map(item => {
    const parts = item.data.split('-');
    const dataFormatada = parts.length === 3 ? `${parts[2]}/${parts[1]}` : item.data;
    return {
      dataHora: `${dataFormatada} ${item.hora}`,
      "Pessoas QG": item.pessoasQG,
      "Oficiais": item.oficiaisCount,
    };
  }) || [];

  // Agrupar relatorios por dia para o card Detalhes RM
  const relatoriosPorDia = React.useMemo(() => {
    const todosRelatorios = data.responsaveis
      .flatMap(r => r.relatorios.map(rel => ({ ...rel, criadorNome: r.militar.nome })));

    const diasMap = new Map<string, typeof todosRelatorios>();
    todosRelatorios.forEach(rel => {
      const dia = toDateStr(rel.data);
      if (!diasMap.has(dia)) diasMap.set(dia, []);
      diasMap.get(dia)!.push(rel);
    });

    // Ordenar dias e horários dentro de cada dia
    return Array.from(diasMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dia, rels]) => ({
        dia,
        relatorios: [...rels].sort((a, b) => (a.horario || '').localeCompare(b.horario || '')),
        militaresUnicos: new Set(rels.flatMap(r => (r.oficiais || []).map(o => o.id))).size,
      }));
  }, [data.responsaveis]);

  return (
    <div className="space-y-6">
      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Relatórios</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.resumo.totalRelatorios}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Média por Dia</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.resumo.mediaRelatoriosPorDia.toFixed(1)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Tempo Médio Ativo</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatMinutos(data.resumo.tempoMedioAtivo)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos lado a lado */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Pessoas no QG por Data/Hora</CardTitle>
            <CardDescription>Quantidade de pessoas no QG por horário</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={distribuicaoHorariaData} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="currentColor" strokeOpacity={0.06} />
                <XAxis dataKey="dataHora" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Pessoas QG" fill="oklch(0.55 0.15 262)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Oficiais no QG por Data/Hora</CardTitle>
            <CardDescription>Quantidade de oficiais presentes por horário</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={distribuicaoHorariaData} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="currentColor" strokeOpacity={0.06} />
                <XAxis dataKey="dataHora" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Oficiais" fill="oklch(0.5 0.15 30)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Responsáveis RM */}
      <Card>
        <CardHeader>
          <CardTitle>Responsáveis RM</CardTitle>
          <CardDescription>Desempenho dos responsáveis por relatório de movimento</CardDescription>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground text-xs">
                <th className="text-left pb-2 w-8">#</th>
                <th className="text-left pb-2">Militar</th>
                <th className="text-left pb-2">Patente</th>
                <th className="text-right pb-2">Tempo na Função</th>
                <th className="text-right pb-2">Relatórios</th>
                <th className="text-right pb-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {[...data.responsaveis]
                .sort((a, b) => {
                  const tempoA = a.tempoAtivoPorDia.reduce((acc, d) => acc + d.minutos, 0);
                  const tempoB = b.tempoAtivoPorDia.reduce((acc, d) => acc + d.minutos, 0);
                  return tempoB - tempoA;
                })
                .map((responsavel, index) => {
                  const totalMinutos = responsavel.tempoAtivoPorDia.reduce((acc, d) => acc + d.minutos, 0);
                  const horas = Math.floor(totalMinutos / 60);
                  const minutos = Math.round(totalMinutos % 60);
                  return (
                    <tr key={responsavel.militar.id} className="border-b border-border/50 last:border-0 hover:bg-secondary/20 transition-colors">
                      <td className="py-2 text-muted-foreground text-xs">{index + 1}</td>
                      <td className="py-2 font-medium">{responsavel.militar.nome}</td>
                      <td className="py-2 text-muted-foreground">{responsavel.militar.patente}</td>
                      <td className="py-2 text-right font-bold">{horas}h {minutos}m</td>
                      <td className="py-2 text-right">{responsavel.totalRelatorios}</td>
                      <td className="py-2 text-right">
                        {responsavel.totalRelatorios > 0
                          ? <span className="text-green-600 dark:text-green-400 text-xs">✓ Enviou</span>
                          : <span className="text-red-500 dark:text-red-400 text-xs">✗ Não enviou</span>
                        }
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Detalhes RM — agrupado por dia */}
      <Card>
        <CardHeader>
          <CardTitle>Detalhes RM</CardTitle>
          <CardDescription>Registros de relatório de movimento agrupados por dia</CardDescription>
        </CardHeader>
        <CardContent>
          {relatoriosPorDia.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">Nenhum registro encontrado.</p>
          ) : (
            <div className="space-y-1">
              {relatoriosPorDia.map(({ dia, relatorios, militaresUnicos }) => {
                const isExpanded = expandedDias.has(dia);
                return (
                  <div key={dia} className="border border-border rounded-lg overflow-hidden">
                    {/* Linha do dia */}
                    <button
                      onClick={() => toggleDia(dia)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-secondary/30 hover:bg-secondary/50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded
                          ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                          : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                        }
                        <span className="font-semibold">{formatDateBR(dia)}</span>
                        <span className="text-sm text-muted-foreground">{relatorios.length} relatório(s)</span>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {militaresUnicos} militar(es) único(s)
                      </span>
                    </button>

                    {/* Horários expandidos */}
                    {isExpanded && (
                      <div className="divide-y divide-border">
                        {relatorios.map(rel => {
                          const qtdOficiais = rel.oficiais?.length ?? rel.oficiaisOnline;
                          return (
                            <div key={rel.id} className="px-4 py-3">
                              <div className="flex items-start justify-between gap-4 mb-2">
                                <div className="flex items-center gap-3">
                                  <span className="font-medium text-sm min-w-[50px]">{rel.horario}</span>
                                  <span className="text-sm text-muted-foreground">{rel.pessoasQG} no QG</span>
                                  <span className="text-sm text-muted-foreground">{qtdOficiais} oficial(is)</span>
                                  {rel.printUrl && (
                                    <a
                                      href={rel.printUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-600 text-xs"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      Print
                                    </a>
                                  )}
                                </div>
                                <span className="text-xs text-muted-foreground shrink-0">{rel.criadorNome}</span>
                              </div>
                              {rel.oficiais && rel.oficiais.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-1 pl-[62px]">
                                  {rel.oficiais.map(of => (
                                    <span
                                      key={of.id}
                                      className="inline-block bg-secondary text-secondary-foreground rounded px-2 py-0.5 text-xs"
                                    >
                                      {of.nick}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

